/**
 * 模块: useSearchPerformance Fetch Handlers
 * 职责: 数据获取处理函数
 * 限制: <280行
 */

import type { MessageInstance } from 'antd/es/message/interface';
import { searchPerformanceAPI, AnalysisParams } from '../../services/searchPerformanceAPI';
import type {
  SearchPerformancePeriod,
  AdvantageKeywordsResult,
  MarketOverview,
  OpportunityKeywordsResult,
  FunnelAnalysis,
  DeliveryAnalysis,
  MarketTrends,
} from '../../types';

/**
 * 安全提取数组数据
 */
const extractArrayData = <T>(data: any): T[] => {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === 'object' && Array.isArray(data.results)) {
    return data.results;
  }
  return [];
};

/**
 * 创建通用fetch处理器
 */
const createFetchHandler = <T>(
  fetchFn: (skuId: number, params?: AnalysisParams) => Promise<{ data: any }>,
  setData: (data: T) => void,
  setLoading: (loading: boolean) => void,
  errorMessage: string,
  message: MessageInstance,
  transform?: (data: any) => T
) => {
  return async (skuId: number, params?: AnalysisParams) => {
    setLoading(true);
    try {
      const response = await fetchFn(skuId, params);
      const data = transform ? transform(response.data) : response.data;
      setData(data);
    } catch (error: any) {
      message.error(`${errorMessage}: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };
};

/**
 * 获取周期列表处理器
 */
export const createFetchPeriodsHandler = (
  skuId: number,
  setPeriods: (periods: SearchPerformancePeriod[]) => void,
  setPeriodsLoading: (loading: boolean) => void,
  message: MessageInstance
) => {
  return async () => {
    setPeriodsLoading(true);
    try {
      const response = await searchPerformanceAPI.getPeriods(skuId);
      setPeriods(extractArrayData(response.data));
    } catch (error: any) {
      message.error(`获取周期列表失败: ${error.response?.data?.error || error.message}`);
    } finally {
      setPeriodsLoading(false);
    }
  };
};

/**
 * 优势关键词处理器
 */
export const createFetchAdvantageKeywordsHandler = (
  skuId: number,
  setData: (data: AdvantageKeywordsResult) => void,
  setLoading: (loading: boolean) => void,
  message: MessageInstance
) => createFetchHandler(
  searchPerformanceAPI.getAdvantageKeywords,
  setData,
  setLoading,
  '获取优势关键词失败',
  message
);

/**
 * 大盘概况处理器
 */
export const createFetchMarketOverviewHandler = (
  skuId: number,
  setData: (data: MarketOverview) => void,
  setLoading: (loading: boolean) => void,
  message: MessageInstance
) => createFetchHandler(searchPerformanceAPI.getMarketOverview, setData, setLoading, '获取大盘概况失败', message);

/**
 * 机会词挖掘处理器
 */
export const createFetchOpportunityKeywordsHandler = (
  skuId: number,
  setData: (data: OpportunityKeywordsResult) => void,
  setLoading: (loading: boolean) => void,
  message: MessageInstance
) => createFetchHandler(
  searchPerformanceAPI.getOpportunityKeywords,
  setData,
  setLoading,
  '获取机会词挖掘失败',
  message
);

/**
 * 转化漏斗处理器
 */
export const createFetchFunnelAnalysisHandler = (
  skuId: number,
  setData: (data: FunnelAnalysis) => void,
  setLoading: (loading: boolean) => void,
  message: MessageInstance
) => createFetchHandler(searchPerformanceAPI.getFunnelAnalysis, setData, setLoading, '获取转化漏斗失败', message);

/**
 * 配送分析处理器
 */
export const createFetchDeliveryAnalysisHandler = (
  skuId: number,
  setData: (data: DeliveryAnalysis) => void,
  setLoading: (loading: boolean) => void,
  message: MessageInstance
) => createFetchHandler(searchPerformanceAPI.getDeliveryAnalysis, setData, setLoading, '获取配送分析失败', message);

/**
 * 市场趋势处理器
 */
export const createFetchMarketTrendsHandler = (
  skuId: number,
  setData: (data: MarketTrends) => void,
  setLoading: (loading: boolean) => void,
  message: MessageInstance
) => createFetchHandler(searchPerformanceAPI.getMarketTrends, setData, setLoading, '获取市场趋势失败', message);

/**
 * 关键词透视表处理器
 */
export const createFetchKeywordPivotHandler = (
  skuId: number,
  setData: (data: any) => void,
  setLoading: (loading: boolean) => void,
  message: MessageInstance
) => {
  return async () => {
    setLoading(true);
    try {
      const response = await searchPerformanceAPI.getKeywordPivot(skuId);
      setData(response.data);
    } catch (error: any) {
      message.error(`获取关键词透视表失败: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };
};
